import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import LazyImage from './LazyImage';
import { useTheme } from '../contexts/ThemeContext';
import { useBooking } from '../contexts/BookingContext';

const SharedFooter = ({ variant = 'default' }) => {
  const currentYear = new Date().getFullYear();
  const { isDark } = useTheme();
  const { openBooking } = useBooking();

  const footerVariants = {
    default: {
      bgClass: 'bg-gradient-to-t from-background-alt to-background',
      textClass: 'text-text-primary',
      linkClass: 'text-text-secondary hover:text-primary transition-colors duration-200',
      borderClass: 'border-border'
    },
    secondary: {
      bgClass: 'bg-gradient-to-t from-background-alt to-background',
      textClass: 'text-text-primary',
      linkClass: 'text-text-secondary hover:text-primary transition-colors duration-200',
      borderClass: 'border-border'
    }
  };

  const currentVariant = footerVariants[variant] || footerVariants.default;

  return (
    <footer className={`${currentVariant.bgClass} py-16 relative`} role="contentinfo">
      <div className="container-executive relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-8 gap-y-12">
          {/* Company Info - Enhanced accessibility */}
          <div className="flex flex-col items-start md:col-span-1">
            <div className="mb-8">
              <Link 
                to="/" 
                className="inline-block mb-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md"
                aria-label="Bootminds - Return to homepage"
              >
                <picture>
                  <source
                    srcSet={isDark ? "/assets/images/White_logo_Bootminds.webp" : "/assets/images/Black_logo_Bootminds.webp"}
                    type="image/webp"
                  />
                  <img
                    src={isDark ? "/assets/images/White_logo_Bootminds.png" : "/assets/images/Black_logo_Bootminds.png"}
                    alt="Bootminds - Global Capability Center consulting company logo"
                    width="200"
                    height="36"
                    className="h-10 w-auto"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </Link>
              <p className={`text-sm leading-relaxed ${currentVariant.linkClass}`}>
                Bootminds helps global enterprises launch and expand India GCCs with transparent pricing, zero-risk engagement, and SkillStack™ staffing.
              </p>
            </div>
            <address className="space-y-2 not-italic">
              <div className={`flex items-center ${currentVariant.linkClass}`}>
                <Icon name="MapPin" size={16} className="mr-2" aria-hidden="true" />
                <span className="text-sm">
                  1000 Paseo Camarillo, Suite 215<br />
                  Camarillo, CA 93010<br />
                  United States
                </span>
              </div>
              <div className={`flex items-center ${currentVariant.linkClass}`}>
                <Icon name="MapPin" size={16} className="mr-2" aria-hidden="true" />
                <span className="text-sm">
                  101-101, F2 Mahagony, Manyata Tech Park<br />
                  Nagwara, Bengaluru 560045<br />
                  India
                </span>
              </div>
            </address>
          </div>

          {/* GCC Services */}
          <div className="flex flex-col items-start">
            <h4 className={`text-lg font-semibold mb-4 text-left p-0 m-0 ${currentVariant.textClass}`}>
              GCC Services
            </h4>
            <nav className="flex flex-col items-start space-y-1 text-sm" aria-label="GCC Services navigation">
              <Link 
                to="/services" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Executive Edition GCC setup"
              >
                Executive Edition
              </Link>
              <Link 
                to="/services#launch-blueprint" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="GCC Launch Blueprint"
              >
                Launch Blueprint
              </Link>
              <Link 
                to="/services/expansion" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="GCC Expansion Services"
              >
                GCC Expansion
              </Link>
              <Link 
                to="/services/staffing" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="SkillStack Staffing Solutions"
              >
                SkillStack Staffing
              </Link>
              <Link 
                to="/services#engagement-models" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Engagement Models"
              >
                Engagement Models
              </Link>
            </nav>
          </div>

          {/* Tools & Resources */}
          <div className="flex flex-col items-start">
            <h4 className={`text-lg font-semibold mb-4 text-left p-0 m-0 ${currentVariant.textClass}`}>
              Tools & Resources
            </h4>
            <nav className="flex flex-col items-start space-y-1 text-sm" aria-label="Tools & Resources navigation">
              <Link 
                to="/calculator" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="ROI Calculator Hub"
              >
                ROI Calculator
              </Link>
              <Link 
                to="/market-intelligence" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Market Intelligence Hub"
              >
                Market Intelligence
              </Link>
              <Link 
                to="/case-studies" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Success Stories"
              >
                Success Stories
              </Link>
              <Link 
                to="/resources" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Industry Insights"
              >
                Industry Insights
              </Link>
            </nav>
          </div>

          {/* Company */}
          <div className="flex flex-col items-start">
            <h4 className={`text-lg font-semibold mb-4 text-left p-0 m-0 ${currentVariant.textClass}`}>
              Company
            </h4>
            <nav className="flex flex-col items-start space-y-1 text-sm" aria-label="Company navigation">
              <Link 
                to="/about" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="About Us"
              >
                About Us
              </Link>
              <Link 
                to="/about#success-stories" 
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Client Success Stories"
              >
                Client Stories
              </Link>
              <button 
                onClick={() => openBooking('footer')}
                className={`block text-left p-0 min-h-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Book a Strategy Call"
              >
                Book Strategy Call
              </button>
              <a 
                href="https://career.bootminds.com" 
                target="_blank"
                rel="noopener noreferrer"
                className={`block p-0 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md ${currentVariant.linkClass}`}
                aria-label="Careers at Bootminds"
              >
                Career
              </a>
            </nav>
          </div>
        </div>

        {/* Footer Bottom - Enhanced accessibility */}
        <div className={`border-t ${currentVariant.borderClass} mt-8 pt-8 flex flex-col md:flex-row justify-between items-center md:items-start gap-y-4 md:gap-y-0`}>
          <div className={`text-sm ${currentVariant.linkClass}`}>
            &copy; {currentYear} Bootminds. All rights reserved.
          </div>
          <nav className={`flex items-center space-x-6 mt-4 md:mt-0 text-sm ${currentVariant.linkClass}`} aria-label="Legal and privacy navigation">
            <Link
              to="/privacy-policy"
              className={`transition-colors duration-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md`}
              aria-label="View our privacy policy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className={`transition-colors duration-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md`}
              aria-label="Read our terms of service"
            >
              Terms of Service
            </Link>
            <Link
              to="/cookies-policy"
              className={`transition-colors duration-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background-alt rounded-md`}
              aria-label="Learn about our cookie policy"
            >
              Cookie Policy
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default SharedFooter;